/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/components/Layout/Header.tsx
 * DESCRIPTION: Top navigation header with logo, workspace indicator,
 *              notification center, dark-mode toggle, user menu, and
 *              mobile sidebar toggle.
 *
 * FEATURES (v2.0.0):
 *   - Company logo always visible (mobile + desktop)
 *   - Search bar removed (per UX decision)
 *   - Live Notification Center (bell + dropdown panel, unread badge,
 *     mark-as-read, dismiss, mark-all, clear-all)
 *   - Workspace scope indicator (global / personal / organization)
 *   - Dark / light mode toggle (persisted to localStorage)
 *   - User dropdown (Profile, Billing, Settings, Sign Out)
 *   - Mobile sidebar toggle (wired via onToggleSidebar prop)
 *
 * ROADMAP STATUS (Data Segregation Sub-Project):
 *   - Phase 1: Workspace scope indicator ✅ (consumes WorkspaceContext)
 *   - Phase 1: Notification center foundation ✅
 *   - Phase 2: Notifications wired to Dashboard/Profile/Org events 🔄
 *   - Phase 3: Notification persistence migrated to Supabase ⏳
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 2.0.0
 * @created 2025-01-15
 * @updated 2026-09-05 - Header rewrite: removed search bar, added
 *                       always-visible logo, integrated Notification
 *                       Center, wired workspace scope indicator.
 * ─────────────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser, useClerk, useOrganization, useOrganizationList } from '@clerk/react';
import {
  Menu,
  Bell,
  Sun,
  Moon,
  User as UserIcon,
  CreditCard,
  Settings,
  LogOut,
  ChevronDown,
  X,
  Check,
  CheckCheck,
  Trash2,
  AlertCircle,
  Info,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import { useNotifications, type AppNotification } from '../../contexts/NotificationContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import ScopeSwitcher from '../ScopeSwitcher';

// ─────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────
interface HeaderProps {
  onToggleSidebar?: () => void;  
}

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

/** Icon + color for a notification type. */
const notificationIcon = (type: AppNotification['type']) => {
  switch (type) {
    case 'success':
      return <CheckCircle className="w-5 h-5 text-emerald-500 dark:text-emerald-400 shrink-0" />;
    case 'error':
      return <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 shrink-0" />;
    case 'warning':
      return <AlertTriangle className="w-5 h-5 text-amber-500 dark:text-amber-400 shrink-0" />;
    case 'info':
    default:
      return <Info className="w-5 h-5 text-blue-500 dark:text-blue-400 shrink-0" />;
  }
};

/** Relative time label (e.g. "2m ago", "3h ago", "yesterday"). */
const timeAgo = (timestamp: number): string => {
  const diffMs = Date.now() - timestamp;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(timestamp).toLocaleDateString();
};

// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────
const Header: React.FC<HeaderProps> = ({ onToggleSidebar }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { signOut } = useClerk();
  const { organization } = useOrganization();
  const { setActive } = useOrganizationList();

  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    dismiss,
    clearAll,
  } = useNotifications();

  const { scope, activeOrgName } = useWorkspace();

  // ── Local UI state ──────────────────────────────────────────────
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  // Refs for outside-click handling
  const profileRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  // ── Dark mode initialization ───────────────────────────────────
  useEffect(() => {
    const stored = localStorage.getItem('darkMode');
    const initial = stored !== null
      ? JSON.parse(stored)
      : window.matchMedia('(prefers-color-scheme: dark)').matches;
    setIsDarkMode(initial);
  }, []);

  const toggleDarkMode = () => {
    const next = !isDarkMode;
    setIsDarkMode(next);
    localStorage.setItem('darkMode', JSON.stringify(next));
    if (next) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  // ── Outside-click to close dropdowns ───────────────────────────
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (profileRef.current && !profileRef.current.contains(target)) {
        setIsProfileDropdownOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(target)) {
        setIsNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Handlers ───────────────────────────────────────────────────
  const handleSignOut = async () => {
    await signOut();
    navigate('/sign-in');
  };

  const handleNotificationClick = useCallback((n: AppNotification) => {
    if (!n.read) markAsRead(n.id);
    if (n.actionPath) {
      navigate(n.actionPath);
      setIsNotificationsOpen(false);
    }
  }, [markAsRead, navigate]);

  // ───────────────────────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────────────────────
  return (
    <header className="sticky top-0 z-40 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* ── LEFT SIDE ─────────────────────────────────────── */}
          <div className="flex items-center gap-3 min-w-0">
            {/* Mobile menu button */}
            <button
              onClick={() => onToggleSidebar?.()}
              className="lg:hidden p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Toggle sidebar"
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* Logo — ALWAYS visible on all screen sizes */}
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <img
                src="/logo.svg"
                alt="Originify"
                className="h-8 w-auto object-contain"
              />
              <span className="hidden sm:inline text-lg font-bold text-gray-900 dark:text-white">
                Originify
              </span>
            </Link>

            {/* Workspace scope switcher (Phase 2.1) */}
            <div className="hidden sm:block">
              <ScopeSwitcher />
            </div>
          </div>

          {/* ── RIGHT SIDE ────────────────────────────────────── */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Notification Bell */}
            <div className="relative" ref={notificationsRef}>
              <button
                onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                className="relative p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Notifications"
                aria-expanded={isNotificationsOpen}
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 dark:bg-red-600 rounded-full">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notification dropdown panel */}
              {isNotificationsOpen && (
                <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Notifications
                      {unreadCount > 0 && (
                        <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                          ({unreadCount} unread)
                        </span>
                      )}
                    </h3>
                    <button
                      onClick={() => setIsNotificationsOpen(false)}
                      className="p-1 rounded text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                      aria-label="Close"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Action bar (only if there are notifications) */}
                  {notifications.length > 0 && (
                    <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/30 text-xs">
                      <button
                        onClick={markAllAsRead}
                        disabled={unreadCount === 0}
                        className="flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                      >
                        <CheckCheck className="w-3.5 h-3.5" />
                        Mark all read
                      </button>
                      <button
                        onClick={clearAll}
                        className="flex items-center gap-1 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Clear all
                      </button>
                    </div>
                  )}

                  {/* Notification list */}
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-10 text-center">
                        <Bell className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No notifications yet
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          Activity from your workspace will appear here.
                        </p>
                      </div>
                    ) : (
                      <ul className="divide-y divide-gray-100 dark:divide-gray-700/50">
                        {notifications.map((n) => (
                          <li
                            key={n.id}
                            className={`group relative hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${
                              !n.read ? 'bg-blue-50/40 dark:bg-blue-900/10' : ''
                            }`}
                          >
                            <button
                              onClick={() => handleNotificationClick(n)}
                              className="w-full text-left px-4 py-3 flex gap-3"
                            >
                              {notificationIcon(n.type)}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <p
                                    className={`text-sm ${
                                      !n.read
                                        ? 'font-semibold text-gray-900 dark:text-white'
                                        : 'font-medium text-gray-800 dark:text-gray-200'
                                    }`}
                                  >
                                    {n.title}
                                    {!n.read && (
                                      <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-blue-500 align-middle" />
                                    )}
                                  </p>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">
                                  {n.message}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                  {timeAgo(n.createdAt)}
                                </p>
                              </div>
                            </button>

                            {/* Per-item actions */}
                            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-200 dark:border-gray-600 p-0.5">
                              {!n.read && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markAsRead(n.id);
                                  }}
                                  className="p-1 rounded text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                  title="Mark as read"
                                >
                                  <Check className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  dismiss(n.id);
                                }}
                                className="p-1 rounded text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                title="Dismiss"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Dark / Light Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            {/* User Dropdown */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-expanded={isProfileDropdownOpen}
              >
                {user?.imageUrl ? (
                  <img
                    src={user.imageUrl}
                    alt={user.fullName || 'User'}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-sm font-semibold">
                    {user?.firstName?.[0] || user?.primaryEmailAddress?.emailAddress?.[0]?.toUpperCase() || 'U'}
                  </div>
                )}
                <span className="hidden md:block text-sm font-medium text-gray-700 dark:text-gray-300 max-w-[120px] truncate">
                  {user?.firstName || user?.primaryEmailAddress?.emailAddress?.split('@')[0] || 'User'}
                </span>
                <ChevronDown className="hidden md:block w-4 h-4 text-gray-500 dark:text-gray-400" />
              </button>

              {/* Dropdown panel */}
              {isProfileDropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
                  {/* User info header */}
                  <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {user?.fullName || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {user?.primaryEmailAddress?.emailAddress}
                    </p>
                  </div>

                  {/* Menu items */}
                  <div className="py-1">
                    <button
                      onClick={() => {
                        navigate('/profile');
                        setIsProfileDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg mx-1 transition-colors"
                      style={{ width: 'calc(100% - 0.5rem)' }}
                    >
                      <UserIcon className="w-4 h-4" />
                      Profile
                    </button>
                    <button
                      onClick={() => {
                        navigate('/billing');
                        setIsProfileDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg mx-1 transition-colors"
                      style={{ width: 'calc(100% - 0.5rem)' }}
                    >
                      <CreditCard className="w-4 h-4" />
                      Billing
                    </button>
                    <button
                      onClick={() => {
                        navigate('/profile');
                        setIsProfileDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg mx-1 transition-colors"
                      style={{ width: 'calc(100% - 0.5rem)' }}
                    >
                      <Settings className="w-4 h-4" />
                      Settings
                    </button>
                  </div>

                  <div className="border-t border-gray-100 dark:border-gray-700 py-1">
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg mx-1 transition-colors"
                      style={{ width: 'calc(100% - 0.5rem)' }}
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;